import React, { Fragment } from "react";

import { starRenderer } from "./_stars.jsx";
import { linkRenderer } from "./_link.jsx";
import { stringRenderer } from "./_string.jsx";
import { textAreaRenderer } from "./_text_area.jsx";
import { booleanRenderer } from "./_boolean.jsx";
import { addressRenderer } from "./_address.jsx";
import { phoneRenderer } from "./_phone.jsx";
import { emailRenderer } from "./_email.jsx";
import { rangeRenderer } from "./_range.jsx";
import { enumRenderer } from "./_enum.jsx";
import { passwordRenderer } from "./_password.jsx";
import { keyValueRenderer } from "./_keyValue.jsx";
import { relationRenderer } from "./_relation.jsx";
import { numberRenderer } from "./_number.jsx";
import { htmlRenderer } from "./_html.jsx";
import { dateRenderer } from "./_date.jsx";
import { dateTimeRenderer } from "./_date_time.jsx";
import { listObjectsRenderer } from "./_list_objects.jsx";
import { jsonRenderer } from "./_json.jsx";
import { dropRenderer } from "./_drop.jsx";
import { priceRenderer } from "./_price.jsx";
import { servicesRenderer } from "./_services.jsx";
import { imageRenderer } from "./_image.jsx";
import { imageCompanyRenderer } from "./_image_company.jsx";
import { listWithCategoriesRenderer } from "./_list_with_categories.jsx";
import { rateInheritingHandler } from "./_rate-inheriting.jsx";
import { additionalSeats } from "./_additional-seats.jsx";
import { baseRateDataRenderer } from "./_base_rate_data.jsx";
import { dependentRatesRenderer } from "./_dependent_rates.jsx";
import { selectWithRatesRenderer } from "./_select_with_rates.jsx";
import { ColorPicker } from "./_color.jsx";
import { stocksRenderer } from "./_stocks.jsx";
import { colorBarRenderer } from "./_color_bar.jsx";
import buttonOpenModalCancellationPolicy from "./buttonOpenModalCancellationPolicy.jsx";
import { PeriodExclusionRenderer } from "./periodExclusionRenderer.jsx";

const renderField = (
    field,
    fieldUpdateHandler,
    errorFields,
    mutable,
    defaultPaymentTypes,
    defaultServices,
    serviceCategories,
    defaultValues,
    updateImage,
    defaultRoomFeatures,
    defaultRoomTypes,
    updateCurRateFromBase,
    allItems,
    inheritStarted,
    unusedServicesHidden,
    defaultRates,
    cancellationPolicy,
    defaultDiscountTypes,
    imageAcceptExtensions,
    defaultHotels,
    placesSale,
    interfaceTypes,
    rates,
    setUpdateRates,
    hotelId,
    refund,
    realHotelId,
) => {
    if (!field) return <Fragment>{""}</Fragment>;
    if (typeof field === "string" || typeof field === "number") {
        return <Fragment>{field}</Fragment>;
    }
    // if (typeof fieldUpdateHandler !== "function") {
    //     return <Fragment>{field.value}</Fragment>;
    // }

    // if (field.editable === false) {
    //     return <Fragment>{field.value}</Fragment>;
    // }

    const isError =
        errorFields &&
        typeof errorFields === "object" &&
        errorFields[field.name]
            ? errorFields[field.name]
            : null;

    const rendererParams = [
        field.value,
        field.name,
        fieldUpdateHandler,
        isError,
        mutable,
        field.editable,
    ];

    switch (field.type) {
        case "string":
            return stringRenderer(...rendererParams);
        case "color_bar":
            return colorBarRenderer(...rendererParams);
        case "text_area":
            return textAreaRenderer(...rendererParams);
        case "number":
            return numberRenderer(...rendererParams);
        case "stocks":
            return stocksRenderer(...rendererParams);
        case "boolean":
            return booleanRenderer(
                field.value,
                field.name,
                field.editable,
                fieldUpdateHandler,
                isError,
                mutable,
                setUpdateRates,
                field.readOnly,
            );
        case "link":
            return linkRenderer(...rendererParams);
        case "address":
            return addressRenderer(...rendererParams);
        case "stars":
            return starRenderer(
                field.value,
                (value) => fieldUpdateHandler(field.name, value),
                mutable,
            );
        case "html":
            return htmlRenderer(...rendererParams);
        case "phone":
            return phoneRenderer(...rendererParams);
        case "email":
            return emailRenderer(...rendererParams);
        case "date":
            return dateRenderer(
                field.value,
                field.name,
                field.restrictionMin,
                field.restrictionMax,
                fieldUpdateHandler,
                mutable,
                field.restrictionFieldName,
                isError,
            );
        case "date_time":
            return dateTimeRenderer(
                field.value,
                field.name,
                field.restrictionMin,
                field.restrictionMax,
                fieldUpdateHandler,
                mutable,
                field.restrictionFieldName,
                isError,
            );
        case "range":
            return rangeRenderer(
                field.value,
                field.name,
                field.min,
                field.max,
                field.restrictionMin,
                field.restrictionMax,
                fieldUpdateHandler,
                field.editable,
                mutable,
            );

        case "enum":
            return enumRenderer(
                field.value,
                field.name,
                field.values,
                field.default,
                fieldUpdateHandler,
                mutable,
                isError,
            );
        case "select_with_rates":
            return selectWithRatesRenderer(
                field.value,
                defaultRates,
                field.name,
                fieldUpdateHandler,
            );
        case "select_with_policy":
            return selectWithRatesRenderer(
                field.value,
                cancellationPolicy,
                field.name,
                fieldUpdateHandler,
            );
        case "select_with_hotels":
            return selectWithRatesRenderer(
                field.value,
                defaultHotels,
                field.name,
                fieldUpdateHandler,
            );
        case "select_with_interface_types":
            return selectWithRatesRenderer(
                field.value,
                interfaceTypes,
                field.name,
                fieldUpdateHandler,
            );
        case "password":
            return passwordRenderer(
                field.values,
                field.name,
                fieldUpdateHandler,
                isError,
                mutable,
            );
        case "list_objects":
            return listObjectsRenderer(
                field.value,
                field.name,
                fieldUpdateHandler,
                mutable,
                defaultPaymentTypes,
                isError,
            );
        case "color":
            return ColorPicker(...rendererParams);
        case "list_with_categories":
            return listWithCategoriesRenderer(
                field.value,
                field.name,
                fieldUpdateHandler,
                mutable,
                defaultRoomFeatures,
            );
        case "list_with_types":
            return listObjectsRenderer(
                field.value,
                field.name,
                fieldUpdateHandler,
                mutable,
                defaultRoomTypes,
                isError,
            );
        case "list_with_rates":
            return listObjectsRenderer(
                field.value,
                field.name,
                fieldUpdateHandler,
                mutable,
                defaultRates,
                isError,
            );
        case "list_with_discount_types":
            return listObjectsRenderer(
                field.value,
                field.name,
                fieldUpdateHandler,
                mutable,
                defaultDiscountTypes,
            );
        case "list_with_hotels":
            return listObjectsRenderer(
                field.value,
                field.name,
                fieldUpdateHandler,
                mutable,
                defaultHotels,
            );
        case "list_with_places_sale":
            return listObjectsRenderer(
                field.value,
                field.name,
                fieldUpdateHandler,
                mutable,
                placesSale,
            );
        case "key-value":
            return keyValueRenderer(
                field.value,
                field.name,
                field.values,
                fieldUpdateHandler,
            );
        case "json":
            return jsonRenderer(
                field.value,
                field.name,
                fieldUpdateHandler,
                mutable,
            );
        case "relation":
            return relationRenderer(field.value, field.dataSource);
        case "drop":
            return dropRenderer(
                field.value,
                field.name,
                fieldUpdateHandler,
                mutable,
                defaultValues,
            );
        case "price":
            return priceRenderer(...rendererParams);
        case "services":
            return servicesRenderer(
                field.value,
                field.name,
                fieldUpdateHandler,
                mutable,
                defaultServices,
                serviceCategories,
                unusedServicesHidden,
            );
        case "image":
            return imageRenderer(
                field.value,
                field.name,
                updateImage,
                field.imageIsLoading,
                mutable,
                imageAcceptExtensions,
                hotelId,
            );
        case "image_company":
            return imageCompanyRenderer(
                field.value,
                field.name,
                updateImage,
                field.imageIsLoading,
                mutable,
                imageAcceptExtensions,
            );
        case "inheritance":
            return rateInheritingHandler(
                allItems,
                updateCurRateFromBase,
                fieldUpdateHandler,
                field.value,
                inheritStarted,
                rates,
                realHotelId,
            );
        case "additional":
            return additionalSeats(field);
        case "base_rate_data":
            return baseRateDataRenderer(field.value);
        case "dependent_rates":
            return dependentRatesRenderer(field.value);
        case "buttonOpenModalCancellationPolicy": {
            return buttonOpenModalCancellationPolicy(
                field.value,
                field.name,
                fieldUpdateHandler,
            );
        }
        case "period_exclusion": {
            return (
              <PeriodExclusionRenderer
                value={field.value}
                name={field.name}
                fieldUpdateHandler={fieldUpdateHandler}
              />
            );

        }
    }

    return <Fragment>{field.value} </Fragment>;
};

export default renderField;
